import React, { Fragment, useState } from "react";
import Button from "components/Form/fields/Button";
import ProductNewPriceForm from "components/ProductForm/ProductNewPriceForm";
import ProductPricesIndex from "components/ProductForm/ProductPricesIndex";
import ProductPriceForm from "components/ProductForm/ProductPriceForm";
import { useCtxReferences, useProduct } from "utils/selectors";
import PricesCurrencySelect from "components/PricesCurrencySelect";
import Toggle from "react-toggle";
import { getToggleProps } from "utils/form";
import { PRODUCT_PRICE_INTENT } from "utils/constants/pricing";
import Callout from "components/Callout";
import {
  filterProductPricesStatusCurrency,
  getInitialActiveCurrency,
  getPriceForCurrencyLocale
} from "utils/price";
import Overlay from "components/Overlay";
import { useModelQueryId } from "utils/hooks";
import { PRICE, PRODUCT } from "utils/constants/models";
import SectionBezel from "components/SectionBezel";
import { BUTTON_CLASS_CONTEXT, BUTTON_TAGS } from "utils/constants/ui";
import { useRouter } from "next/router";
import { isModelCreateRoute } from "utils/route";
import { usePrevPathFragment } from "utils/hooks/hub";

const DEFAULT_CLASSES = {
  container: "flex flex-column w-100 ph3 pv3",
  actionsWrapper: "flex flex-row justify-end items-center"
};

const ProductPricesSection = ({ customClasses }) => {
  const { resource, resourceUUID, merchantAccount } = useCtxReferences();
  const classes = { ...DEFAULT_CLASSES, ...customClasses };
  const { asPath, replace } = useRouter();

  const isCreateRoute = isModelCreateRoute(PRICE, asPath);

  const createPricePath = `${asPath}/prices/create`;
  const productId = useModelQueryId(PRODUCT);
  const product = useProduct(productId);
  const priceId = useModelQueryId(PRICE);
  /**
   * Remove 2 fragments so that we land back on product show
   * - products/uid/prices/uid
   */
  const backPath = usePrevPathFragment(2);

  const activePriceModel =
    product && product.prices.find((price) => price.id === priceId);

  const priceForCurrency = product && getPriceForCurrencyLocale(product.prices);
  const [activeCurrency, setActiveCurrency] = useState(
    getInitialActiveCurrency({
      price: priceForCurrency,
      product: product
    })
  );
  const [showInactive, setShowInactive] = useState(false);

  let content = null;

  const backToIndex = () => {
    replace(backPath);
  };

  const showOverlay = isCreateRoute || activePriceModel;

  const productPrices = filterProductPricesStatusCurrency({
    productUid: productId,
    prices: product.prices,
    currency: activeCurrency,
    showInactive
  });
  const hasInactive = product.prices.find(({ active }) => !active);

  if (activePriceModel) {
    const priceFormProps = {
      uid: priceId,
      product,
      price: activePriceModel,
      goBack: backToIndex,
      resource,
      resourceUUID,
      merchantAccountUUID: merchantAccount && merchantAccount.uuid,
      customClasses: classes.editPrice,
      navToProductPriceCreate: () =>
        setActiveSection(PRODUCT_PRICE_INTENT.NEW_PRICE)
    };
    content = <ProductPriceForm {...priceFormProps} />;
  } else if (isCreateRoute) {
    const newPriceFormProps = {
      uid: productId,
      goBack: backToIndex,
      resource,
      resourceUUID,
      merchantAccountUUID: merchantAccount && merchantAccount.uuid,
      hidePadder: true,
      customClasses: classes.createPrice
    };
    content = <ProductNewPriceForm {...newPriceFormProps} />;
  }

  const actionNode = (
    <div className={classes.actionsWrapper}>
      <div className="pr2">
        <PricesCurrencySelect
          hideLabel
          customClasses={{
            container: "flex flex-row justify-between items-center"
          }}
          selectProps={{
            customClasses: {
              input:
                "border-box input-reset ba br2 b--black-20 pa2 db w-100 pointer shadow-0"
            }
          }}
          prices={product.prices}
          value={activeCurrency}
          setActiveCurrency={setActiveCurrency}
        />
      </div>
      <Button
        tag={BUTTON_TAGS.LINK}
        theme="slim"
        classes={{
          contextClass: BUTTON_CLASS_CONTEXT.SECONDARY
        }}
        href={createPricePath}
        copy="Create"
      />
    </div>
  );

  return (
    <Fragment>
      <SectionBezel header="Prices" action={actionNode}>
        <Fragment>
          {hasInactive && (
            <div className="flex flex-row justify-between items-center">
              <label className="f6 fw5 db">Show inactive prices</label>
              <Toggle
                {...getToggleProps({
                  active: showInactive,
                  setFieldValue: (_, active) => setShowInactive(active)
                })}
              />
            </div>
          )}
        </Fragment>
        {productPrices.length ? (
          <ProductPricesIndex prices={productPrices} />
        ) : (
          <div className="pt3">
            <Callout
              copy="No product prices available."
              customClasses={{
                container:
                  "flex ph2 ba br2 items-center b--black-10 pv2 w-100 justify-between"
              }}
            />
          </div>
        )}
      </SectionBezel>
      {showOverlay && content && (
        <Overlay
          hideOverlay={() => {
            replace(backPath);
          }}
        >
          <div className="pt5">{content}</div>
        </Overlay>
      )}
    </Fragment>
  );
};

export default ProductPricesSection;
